import { useState, useMemo, useEffect, useRef, useContext } from "react";
import { AgGridReact } from "ag-grid-react"; // the AG Grid React Component
import { Button, Modal } from "antd";
import { message } from "utils/ciq-message";
import { InviteSubscriptionUser } from "pages/invite-user/invite-subscription-user";

import {
  CellValueRendererWithEditIcon,
  dummyColumnDef,
  InviteStatusCellRenderer,
  truncatedTextCellRenderer,
  userCompanyRenderer,
  userNameRenderer,
  userRoleRenderer
} from "components/cell-renders";
import {
  RoleSubscriptionEnum,
  SubscriptionContext
} from "context/SubscriptionProvider";
import { ColDef, GetRowIdParams } from "ag-grid-enterprise";
import { GridLoadingIndicator } from "components/widgets";
import UserNameCellEditor from "components/cell-editor/user-name-cell-editor";
import UserRoleSelectCellEditor from "components/cell-editor/user-role-cell-editor";
import {
  MUTATION_UPDATE_SUBSCRIPTION_USER_NAME,
  MUTATION_UPDATE_SUBSCRIPTION_USER_PERMISSION
} from "services/graphQL/mutations";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import InviteAccUserComponent from "pages/invite-user/invite-subscription-user/invite-acc-user";
import CiqLogPageHeader, {
  calculateLogPageStats
} from "components/ciq-log-page-header";
import CiqAgSearch from "components/ciq-ag-search";
import {
  FilterChipComponent,
  FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import { getSubscriptionUserSources } from "utils/utils";
import { getUser } from "services/auth";
import UserCompanySelectCellEditor from "components/cell-editor/user-company-cell-editor";
import {
  QUERY_GET_INTEGRATION_PROJECT_COUNT,
  QUERY_SUBSCRIPTION_USERS,
  QUERY_USER_SUBSCRIPTION_ROLES,
  SUBSCRIPTION_LEVEL_COMPANY_LIST
} from "services/graphQL/ciq-queries";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import {
  AcceptanceStatus,
  DialogTitles,
  ErrorMessages,
  Sources
} from "../../constants";

// Admin can edit all users name
// Editor can edit non-admin users name
// Viewer can't edit
const hasUserInfoEditAccess = (params: any) => {
  const { context, data } = params;
  const sources = getSubscriptionUserSources(data);
  const ciqSourceArr: string[] = sources?.filter(
    (source: string) => source === (Sources.ConstructivIQ as string)
  );
  const isCiqSourceUser = sources.length === ciqSourceArr.length;
  const isPermissionEnabled =
    context.hasAdminAccess ||
    (context.subscriptionRole === RoleSubscriptionEnum.subscription_editor &&
      data?.subscription_permission?.name !== "Admin");
  const isStatusActiveOrPending =
    params.data.status_id === AcceptanceStatus.ACTIVE ||
    params.data.status_id === AcceptanceStatus.INVITED;

  return isCiqSourceUser && isPermissionEnabled && isStatusActiveOrPending;
};

///
// CIQ-5665 1. User Permission in a Subscription can be edited only by Subscription Admins.
// CIQ-5665 2. Permission can be edited of ONLY other users. A user can’t edit his own permission.
///
const hasPermissionCellEditAccess = (params: any) => {
  const { context, data } = params;
  const isStatusActiveOrPending =
    params.data.status_id === AcceptanceStatus.ACTIVE ||
    params.data.status_id === AcceptanceStatus.INVITED;

  const isPermissionEnabled =
    context.hasAdminAccess ||
    (context.subscriptionRole === RoleSubscriptionEnum.subscription_editor &&
      data?.subscription_permission?.name !== "Admin");

  return (
    isPermissionEnabled &&
    data?.user?.email !== context?.currentUser?.email &&
    isStatusActiveOrPending
  );
};

///  After refetch the rowdata ,It help to persist Scroll Bar
const getRowId = (params: GetRowIdParams<{ user_id: string }>) =>
  params.data.user_id;

function Users() {
  const gridRef = useRef<AgGridReact>(null);
  const [isGridReady, setGridReady] = useState(false);
  const [drawer, setDrawerVisibility] = useState<boolean>(false);
  const [allUsers, setUsers] = useState([]);
  const { subscriptionRole, subscriptionId, eventLogs } =
    useContext(SubscriptionContext);
  const [currentUser]: [any, any] = useState(getUser());

  const [inviteAccUser, setInviteAccUser] = useState<{
    showInviteModel: boolean;
    inviteUserData?: any;
  }>({
    showInviteModel: false,
    inviteUserData: null
  });
  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();
  const filterChipRef = useRef<FilterChipComponentRef>(null);

  const hasAdminAccess =
    subscriptionRole === RoleSubscriptionEnum.subscription_admin;

  const {
    data: usersData,
    refetch,
    error,
    loading
  } = useCIQQuery(QUERY_SUBSCRIPTION_USERS);

  const { data: userSubscriptionRoles } = useCIQQuery(
    QUERY_USER_SUBSCRIPTION_ROLES
  );

  const { data: integrationProjectsData } = useCIQQuery(
    QUERY_GET_INTEGRATION_PROJECT_COUNT
  );

  const { data: companyListData, refetch: refetchCompanyList } = useCIQQuery(
    SUBSCRIPTION_LEVEL_COMPANY_LIST,
    {
      fetchPolicy: "network-only"
    }
  );

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs.length && previousEventLogs.current !== eventLogs) {
      if (
        eventLogs.some(
          (log) =>
            log.data_source === "subscription_users" &&
            ["update", "create", "delete"].includes(log.change_type)
        )
      ) {
        refetch();
      }
    }
    if (
      eventLogs.some(
        (log) =>
          log.data_source === "subscription_vendors" &&
          ["update", "create", "delete"].includes(log.change_type)
      )
    ) {
      refetchCompanyList();
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, refetch, refetchCompanyList]);

  const isIntegratedProjectExist = useMemo(() => {
    return integrationProjectsData?.project?.length;
  }, [integrationProjectsData]);

  const [updateUserPermission] = useCIQMutation(
    MUTATION_UPDATE_SUBSCRIPTION_USER_PERMISSION
  );

  const [updateUserInfo] = useCIQMutation(
    MUTATION_UPDATE_SUBSCRIPTION_USER_NAME
  );

  const setDrawerAction = (drawerAction: boolean) => {
    setDrawerVisibility(drawerAction);
  };

  useEffect(() => {
    if (isGridReady && gridRef && gridRef.current) {
      gridRef.current?.api?.setGridOption("loading", loading);
    }
    if (error) {
      gridRef.current?.api?.setGridOption("loading", false);
      message.error(error.message);
      return;
    }
    if (!usersData) return;
    setUsers(usersData.subscription_users);
  }, [error, loading, usersData, isGridReady]);

  // Each Column Definition results in one Column.
  const columnDefs: ColDef[] = useMemo<ColDef[]>(() => {
    return [
      dummyColumnDef,
      {
        headerName: "Serial No.",
        valueGetter: (params) => (params.node?.rowIndex ?? 0) + 1,
        headerTooltip: "Serial No.",
        width: 100,
        sortable: false,
        editable: false,
        menuTabs: [],
        cellRenderer: truncatedTextCellRenderer
      },
      {
        valueGetter: (params: any) => {
          const first = params.data?.user?.first_name ?? "";
          const last = params.data?.user?.last_name ?? "";
          return `${first} ${last}`.trim();
        },
        field: "name",
        headerName: "Name",
        headerTooltip: "Name",

        comparator: (valueA: any, valueB: any) => {
          const a = (valueA ?? "").toString().toLowerCase();
          const b = (valueB ?? "").toString().toLowerCase();
          return a.localeCompare(b);
        },
        editable: hasUserInfoEditAccess,
        cellRenderer: userNameRenderer,
        cellEditor: UserNameCellEditor,
        cellClass: (params: any) => {
          return hasUserInfoEditAccess(params) ? "cell-editable" : "";
        },
        sort: "asc"
      },
      {
        valueGetter: (params: any) => params.data?.user?.email ?? "",
        headerName: "Email",
        headerTooltip: "Email",
        field: "email",
        colId: "email",
        cellRenderer: truncatedTextCellRenderer
      },
      {
        headerName: "Company",
        headerTooltip: "Company",
        filter: true,
        field: "subscription_vendor.name",
        colId: "company",
        editable: hasUserInfoEditAccess,
        cellRenderer: userCompanyRenderer,
        cellEditor: UserCompanySelectCellEditor,
        cellClass: (params: any) => {
          return hasUserInfoEditAccess(params) ? "cell-editable" : "";
        }
      },
      {
        valueGetter: (params: any) => {
          return params.data.subscription_permission?.name;
        },
        field: "permission",
        colId: "permission",
        headerName: "Permission",
        headerTooltip: "Permission",
        filter: true,
        editable: hasPermissionCellEditAccess,
        cellRenderer: userRoleRenderer,
        cellEditor: UserRoleSelectCellEditor,
        cellClass: (params: any) => {
          return hasPermissionCellEditAccess(params) ? "cell-editable" : "";
        }
      },
      {
        valueGetter: (params: any) => params.data?.user?.phone ?? "",
        field: "phone",
        headerName: "Phone Number",
        headerTooltip: "Phone Number",
        colId: "phone",
        editable: hasUserInfoEditAccess,
        cellRenderer: CellValueRendererWithEditIcon,
        cellClass: (params: any) => {
          return hasUserInfoEditAccess(params) ? "cell-editable" : "";
        }
      },
      {
        colId: "source",
        headerName: "Source",
        headerTooltip: "Source",
        hide: !isIntegratedProjectExist,
        valueGetter: ({ data }) => {
          const sources = getSubscriptionUserSources(data);
          return sources.join(",");
        },
        cellRenderer: truncatedTextCellRenderer
      },
      {
        field: "status_id",
        colId: "status_id",
        headerName: "Invite Status",
        headerTooltip: "Invite Status",
        minWidth: 220,
        cellRenderer: InviteStatusCellRenderer,
        cellRendererParams: { inviteType: "Subscription" },
        comparator: (valueA: any, valueB: any) => {
          const a = (AcceptanceStatus[valueA] ?? "").toLowerCase();
          const b = (AcceptanceStatus[valueB] ?? "").toLowerCase();
          return a.localeCompare(b);
        },
        tooltipValueGetter: (params) => {
          let status = "";
          switch (params.data.status_id) {
            case 1:
              status = "Pending";
              break;
            case 2:
              status = "Active";
              break;
            case 3:
              status = "Inactive";
              break;
            case 4:
              status = "Not Invited";
              break;

            default:
              break;
          }
          return status;
        }
      }
    ];
  }, [isIntegratedProjectExist]);

  // DefaultColDef sets props common to all Columns
  const defaultColDef = useMemo(
    () =>
      ({
        sortable: true,
        resizable: true,
        comparator: comparatorWithEmptyAtBottom
      }) as ColDef,
    []
  );

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef && gridRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);
  }, [isGridReady]);

  const onUserInfoCellEdit = async (event: any) => {
    let variables = {};
    const oldData = { ...event.data };
    if (event.column.colId === "name") {
      const firstName = event.newValue.firstNameValue?.trim();
      const lastName = event.newValue.lastNameValue?.trim();

      if (
        firstName === "" ||
        (firstName === oldData.user.first_name &&
          lastName === oldData.user.last_name)
      )
        return;

      event.node.setData({
        ...oldData,
        user: {
          email: event?.data?.user?.email,
          first_name: firstName,
          last_name: lastName,
          phone: event?.data?.user?.phone
        }
      });

      variables = {
        pk_columns: { id: event.data?.user_id },
        _set: { first_name: firstName, last_name: lastName }
      };
    }

    if (event.column.colId === "phone") {
      const firstName = event.data.user?.first_name.trim();
      const lastName = event.data.user?.last_name.trim();
      event.node.setData({
        ...oldData,
        user: {
          email: event?.data?.user?.email,
          first_name: firstName,
          last_name: lastName,
          phone: event.newValue
        }
      });

      variables = {
        pk_columns: { id: event.data?.user_id },
        _set: { phone: event.newValue }
      };
    }
    // caller cellEditRequest only routes here when colId is "name" or "phone"; both populate variables before this point, so it is never empty
    /* v8 ignore next */
    if (!Object.keys(variables).length) return;

    try {
      const updateResponse = await updateUserInfo({
        variables
      });
      if (updateResponse?.data?.update_user_by_pk) {
        message.success("User name updated successfully.");
      } else {
        event.node.setData(oldData);
      }
    } catch {
      message.error("Failed to update");
      event.node.setData(oldData);
    }
  };

  const onUserPermissionCellEdit = async (event: any) => {
    if (event.newValue?.trim() === event.oldValue?.trim()) return;
    const oldData = { ...event.data };
    const userRole = userSubscriptionRoles?.subscription_permission?.find(
      (role: any) => role?.name === event.newValue
    );
    if (userRole?.id) {
      event.node.setData({
        ...oldData,
        subscription_permission: userRole
      });

      const variables = {
        where: {
          subscription_id: { _eq: subscriptionId },
          user_id: { _eq: event.data.user_id }
        },
        _set: { subscription_permission_id: userRole?.id }
      };
      try {
        const updateResponse = await updateUserPermission({
          variables
        });
        if (updateResponse?.data?.update_subscription_users) {
          message.success("User permission updated successfully.");
        } else {
          message.error("Failed to update.");
          event.node.setData(oldData);
        }
      } catch {
        message.error("An error occured");
        event.node.setData(oldData);
      }
    }
  };

  const onUserCompanyEditCell = async (event: any) => {
    if (event.newValue?.trim() === event.oldValue?.trim()) return;
    const oldData = { ...event.data };

    Modal.confirm({
      title: (
        <p className="text-ciq-gray-950 text-sm">
          The User&apos;s Company will be changed at the Subscription level
          ONLY. This change will not update the User&apos;s Company within any
          existing projects. Do you want to proceed?
        </p>
      ),
      okText: "Yes",
      cancelText: "No",
      // className: "skip-listing-window",
      async onOk() {
        const company = companyListData?.subscription_vendors?.find(
          (c: any) => c?.name === event.newValue
        );
        event.node.setData({
          ...oldData,
          subscription_vendor: { name: event.newValue }
        });

        const variables = {
          where: {
            subscription_id: { _eq: subscriptionId },
            user_id: { _eq: event.data.user_id }
          },
          _set: { vendor_id: company?.id }
        };

        try {
          const updateResponse = await updateUserPermission({
            variables
          });
          if (updateResponse?.data?.update_subscription_users) {
            message.success("User company updated successfully.");
          } else {
            message.error("Failed to update.");
            event.node.setData(oldData);
          }
        } catch {
          message.error("An error occured");
          event.node.setData(oldData);
        }
      },
      onCancel: () => {
        event.node.setData({
          ...oldData
        });
      }
    });
  };

  const cellEditRequest = async (event: any) => {
    if (!event.newValue) return;
    if (event.column.colId === "name" || event.column.colId === "phone") {
      onUserInfoCellEdit(event);
      return;
    }
    if (event.column.colId === "company") {
      onUserCompanyEditCell(event);
      return;
    }

    if (event.column.colId === "permission") {
      onUserPermissionCellEdit(event);
    }
  };

  const renderUsers = () => {
    if (usersData && usersData.subscription_users.length === 0) {
      return (
        <div>Start by adding users of your company you want work with</div>
      );
    }

    const onFiltersApplied = () => {
      filterChipRef.current?.onFilterApplied();
      setStatsData(
        calculateLogPageStats({
          gridRef
        })
      );
    };

    return (
      <AgGridReact<any>
        className="ag-theme-alpine"
        ref={gridRef}
        onGridReady={() => {
          setGridReady(true);
          gridRef?.current?.api?.sizeColumnsToFit();
        }}
        rowData={allUsers}
        columnDefs={columnDefs} // Column Defs for Columns
        defaultColDef={defaultColDef} // Default Column Properties
        animateRows={false} // Optional - set to 'true' to have rows animate when sorted
        loadingOverlayComponent={GridLoadingIndicator}
        getRowId={getRowId} // After refetch the rowdata ,It help to persist Scroll Bar
        readOnlyEdit
        singleClickEdit
        onCellEditRequest={cellEditRequest}
        stopEditingWhenCellsLoseFocus
        suppressDragLeaveHidesColumns
        tooltipShowDelay={0}
        tooltipHideDelay={2000}
        tooltipShowMode="whenTruncated"
        context={{
          hasAdminAccess,
          subscriptionRole,
          userSubscriptionRoles,
          setInviteAccUser,
          currentUser,
          subscriptionCompanies: companyListData?.subscription_vendors
        }}
        onFilterChanged={onFiltersApplied}
      />
    );
  };

  const headerItems = [
    <CiqAgSearch
      key="SubscriptionUsers_search"
      gridRef={gridRef}
      placeholder="Search Users"
    />,
    <FilterChipComponent
      key="SubscriptionUsers_FilterChip"
      gridRef={gridRef}
      ref={filterChipRef}
    />,
    <Button
      type="primary"
      key="SubscriptionUsers_AddUser"
      onClick={() => setDrawerVisibility(true)}
      title={
        subscriptionRole <= RoleSubscriptionEnum.subscription_viewer
          ? ErrorMessages.PermissionNotGranted
          : "Add User"
      }
      disabled={subscriptionRole <= RoleSubscriptionEnum.subscription_viewer}
    >
      Add User
    </Button>
  ];

  return (
    <>
      <CiqLogPageHeader
        {...{
          entityName: "Users",
          titleParam: {
            title: "Users",
            totalCount: allUsers?.length,
            filterStats: statsData
          },
          gridRef,
          items: headerItems
        }}
      />
      <div className="grow">{renderUsers()}</div>
      {drawer && (
        <InviteSubscriptionUser
          existingUsers={
            // allUsers starts as useState([]) and is only set to subscription_users; a null/undefined value would crash renderUsers().length first, so the || [] fallback is unreachable
            /* v8 ignore next */
            allUsers || []
          }
          setDrawerOpen={setDrawerAction}
          showDrawerOpen={drawer}
          modelTitle={DialogTitles.addUser}
          refetch={refetch}
        />
      )}
      {inviteAccUser.showInviteModel && (
        <InviteAccUserComponent
          setDrawerOpen={setInviteAccUser}
          showDrawerOpen={inviteAccUser.showInviteModel}
          modelTitle={DialogTitles.inviteUser}
          userData={inviteAccUser.inviteUserData}
          isFromProjectUser={false}
        />
      )}
    </>
  );
}

export default Users;
